import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DictionaryApiService from "../system/integrate/DictionaryApiService";
import Logger from "../../services/Logger";
import ColorService from "../../services/ColorService";
import WordScoreSection from "./WordScoreSection";

const WordScoreDialog = ({
    open,
    onClose,
    wordScores = [],
    loadingScores = false,
    language = "english",
    onRefresh
}) => {
    const [loadingTrends, setLoadingTrends] = useState(false);
    const [trendsError, setTrendsError] = useState(null);
    const [loadingNgram, setLoadingNgram] = useState(false);
    const [ngramError, setNgramError] = useState(null);
    const [difficultyLevel, setDifficultyLevel] = useState('EASY');
    const [loadingDifficulty, setLoadingDifficulty] = useState(false);
    const [policy, setPolicy] = useState(null);
    const [loadingPolicy, setLoadingPolicy] = useState(false);

    // Get words without score info
    const wordsWithoutScore = wordScores
        .filter(wordData => !wordData.info || wordData.info.score === null || wordData.info.score === undefined)
        .map(wordData => wordData.word);

    // Categorize words by difficulty level based on policy
    const wordCategories = useMemo(() => {
        if (!policy || !policy.puzzleConfigure || wordScores.length === 0) {
            return {
                easy: [],
                medium: [],
                hard: [],
                excluded: [],
                noScore: []
            };
        }

        const config = policy.puzzleConfigure;
        const easy = [];
        const medium = [];
        const hard = [];
        const excluded = [];
        const noScore = [];

        wordScores.forEach((wordData) => {
            const score = wordData.info?.score;
            
            if (score === null || score === undefined) {
                noScore.push(wordData);
                return;
            }

            // Exclude words with score < hardScoreMin (excluded from MEDIUM/HARD)
            if (score < config.hardScoreMin) {
                excluded.push(wordData);
                return;
            }

            // Reject words with score > easyScoreMax
            if (score > config.easyScoreMax) {
                excluded.push(wordData);
                return;
            }

            // HARD: score between hardScoreMin and hardScoreMax (inclusive)
            if (score >= config.hardScoreMin && score <= config.hardScoreMax) {
                hard.push(wordData);
                return;
            }

            // MEDIUM: score between mediumScoreMin and mediumScoreMax (inclusive)
            if (score >= config.mediumScoreMin && score <= config.mediumScoreMax) {
                medium.push(wordData);
                return;
            }

            // EASY: score between easyScoreMin and easyScoreMax (inclusive)
            if (score >= config.easyScoreMin && score <= config.easyScoreMax) {
                easy.push(wordData);
                return;
            }

            // Fallback: if score doesn't match any range (e.g., in gap between MEDIUM and EASY), exclude it
            // This handles scores between mediumScoreMax + 1 and easyScoreMin - 1
            excluded.push(wordData);
        });

        return { easy, medium, hard, excluded, noScore };
    }, [wordScores, policy]);

    // Calculate difficulty breakdown
    const difficultyBreakdown = useMemo(() => {
        if (!policy || !policy.puzzleConfigure || wordScores.length === 0) {
            return null;
        }

        const config = policy.puzzleConfigure;
        const wordsWithScore = wordScores.filter(w => w.info?.score !== null && w.info?.score !== undefined);
        const totalWordsWithScore = wordsWithScore.length;

        if (totalWordsWithScore === 0) {
            return null;
        }

        const hardCount = wordCategories.hard.length;
        const easyCount = wordCategories.easy.length;
        const mediumCount = wordCategories.medium.length;

        const hardPercentage = (hardCount / totalWordsWithScore) * 100;
        const easyPercentage = (easyCount / totalWordsWithScore) * 100;

        return {
            totalWords: wordScores.length,
            totalWordsWithScore,
            hardCount,
            mediumCount,
            easyCount,
            excludedCount: wordCategories.excluded.length,
            noScoreCount: wordCategories.noScore.length,
            hardPercentage,
            easyPercentage,
            config
        };
    }, [wordCategories, wordScores, policy]);

    // Fetch difficulty level from API (which also includes policy)
    useEffect(() => {
        const fetchDifficultyLevel = async () => {
            if (wordScores.length === 0) {
                setDifficultyLevel('EASY');
                // If no word scores, fetch policy separately
                try {
                    setLoadingPolicy(true);
                    const policyResult = await DictionaryApiService.getDifficultyLevelPolicy();
                    if (policyResult && policyResult.puzzleConfigure) {
                        setPolicy(policyResult);
                    }
                } catch (error) {
                    Logger.error("Error fetching difficulty level policy:", error);
                } finally {
                    setLoadingPolicy(false);
                }
                return;
            }

            try {
                setLoadingDifficulty(true);
                setLoadingPolicy(true);
                const words = wordScores.map(wordData => wordData.word);
                const result = await DictionaryApiService.getDifficultyLevel(words, language);
                if (result) {
                    // Set difficulty level
                    if (result.difficultyLevel) {
                        setDifficultyLevel(result.difficultyLevel);
                    } else {
                        setDifficultyLevel('EASY');
                    }
                    // Use policy from difficulty-level response if available, otherwise fetch separately
                    if (result.puzzleConfigure) {
                        setPolicy(result);
                    } else {
                        // Fallback: fetch policy separately
                        try {
                            const policyResult = await DictionaryApiService.getDifficultyLevelPolicy();
                            if (policyResult && policyResult.puzzleConfigure) {
                                setPolicy(policyResult);
                            }
                        } catch (error) {
                            Logger.error("Error fetching difficulty level policy:", error);
                        }
                    }
                } else {
                    setDifficultyLevel('EASY');
                }
            } catch (error) {
                Logger.error("Error fetching difficulty level:", error);
                setDifficultyLevel('EASY');
                // Try to fetch policy separately on error
                try {
                    const policyResult = await DictionaryApiService.getDifficultyLevelPolicy();
                    if (policyResult && policyResult.puzzleConfigure) {
                        setPolicy(policyResult);
                    }
                } catch (policyError) {
                    Logger.error("Error fetching difficulty level policy:", policyError);
                }
            } finally {
                setLoadingDifficulty(false);
                setLoadingPolicy(false);
            }
        };

        if (!loadingScores && wordScores.length > 0) {
            fetchDifficultyLevel();
        } else if (!loadingScores && wordScores.length === 0 && open) {
            // Fetch policy when dialog opens but no word scores yet
            fetchDifficultyLevel();
        }
    }, [wordScores, language, loadingScores, open]);

    const handleFetchTrends = async () => {
        if (wordsWithoutScore.length === 0) {
            setTrendsError("No words without score info available");
            return;
        }

        try {
            setLoadingTrends(true);
            setTrendsError(null);

            const result = await DictionaryApiService.getTrends(wordsWithoutScore, language);

            // Check if result has words array (trends API returns { words: [...], language: "..." })
            if (result && (result.words || Array.isArray(result))) {
                Logger.log("Trends API call successful, waiting for backend to save data...");
                // Close dialog and trigger refresh to reload word scores
                // Increased delay to allow backend to process and save the trends data
                onClose();
                if (onRefresh) {
                    // Longer delay to ensure backend has saved the data before refreshing
                    // Increased to 3 seconds to give backend more time to process
                    setTimeout(() => {
                        Logger.log("Refreshing word scores after trends call...");
                        onRefresh();
                    }, 3000);
                }
            } else {
                setTrendsError("Failed to fetch trends data");
            }
        } catch (error) {
            Logger.error("Error fetching trends data:", error);
            setTrendsError(error.message || "Error fetching trends data");
        } finally {
            setLoadingTrends(false);
        }
    };

    const handleFetchNgram = async () => {
        if (wordsWithoutScore.length === 0) {
            setNgramError("No words without score info available");
            return;
        }

        try {
            setLoadingNgram(true);
            setNgramError(null);

            const result = await DictionaryApiService.getNgram(wordsWithoutScore, language);

            // Check if result has words array (ngram API returns { words: [...], language: "..." })
            if (result && (result.words || Array.isArray(result))) {
                Logger.log("Ngram API call successful, waiting for backend to save data...");
                // Close dialog and trigger refresh to reload word scores
                // Increased delay to allow backend to process and save the ngram data
                onClose();
                if (onRefresh) {
                    // Longer delay to ensure backend has saved the data before refreshing
                    // Increased to 3 seconds to give backend more time to process
                    setTimeout(() => {
                        Logger.log("Refreshing word scores after ngram call...");
                        onRefresh();
                    }, 3000);
                }
            } else {
                setNgramError("Failed to fetch ngram data");
            }
        } catch (error) {
            Logger.error("Error fetching ngram data:", error);
            setNgramError(error.message || "Error fetching ngram data");
        } finally {
            setLoadingNgram(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6">Word Scores</Typography>
                    {!loadingScores && wordScores.length > 0 && (
                        <Chip
                            label={difficultyLevel}
                            size="small"
                            sx={{
                                backgroundColor: ColorService.getDifficultyColor(difficultyLevel),
                                color: "white",
                                fontWeight: "bold"
                            }}
                        />
                    )}
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pb: 2 }}>
                {loadingScores ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : wordScores.length === 0 ? (
                    <Typography color="text.secondary" sx={{ p: 2 }}>
                        No scores available
                    </Typography>
                ) : (
                    <>
                        {/* Difficulty Calculation Breakdown */}
                        {difficultyBreakdown && policy && (
                            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Difficulty Calculation
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Total Words:</strong> {difficultyBreakdown.totalWords}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Words with Scores:</strong> {difficultyBreakdown.totalWordsWithScore}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Words without Scores:</strong> {difficultyBreakdown.noScoreCount}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={`HARD: ${difficultyBreakdown.hardCount} (${difficultyBreakdown.hardPercentage.toFixed(1)}%)`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: ColorService.getDifficultyColor('HARD'),
                                                    color: "white",
                                                    fontWeight: "bold"
                                                }}
                                            />
                                            <Chip
                                                label={`MEDIUM: ${difficultyBreakdown.mediumCount}`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: ColorService.getDifficultyColor('MEDIUM'),
                                                    color: "white",
                                                    fontWeight: "bold"
                                                }}
                                            />
                                            <Chip
                                                label={`EASY: ${difficultyBreakdown.easyCount} (${difficultyBreakdown.easyPercentage.toFixed(1)}%)`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: ColorService.getDifficultyColor('EASY'),
                                                    color: "white",
                                                    fontWeight: "bold"
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            HARD if {policy.puzzleConfigure.lowScoreThresholdPercent}%+ words have score ≤ {policy.puzzleConfigure.hardScoreMax}
                                            <br />
                                            EASY if {policy.puzzleConfigure.easyScoreThresholdPercent}%+ words have score ≥ {policy.puzzleConfigure.easyScoreMin}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleFetchNgram}
                                disabled={loadingNgram || wordsWithoutScore.length === 0}
                                sx={{ minWidth: 120 }}
                            >
                                {loadingNgram ? (
                                    <>
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                        Loading...
                                    </>
                                ) : (
                                    "Ngram"
                                )}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleFetchTrends}
                                disabled={loadingTrends || wordsWithoutScore.length === 0}
                                sx={{ minWidth: 120 }}
                            >
                                {loadingTrends ? (
                                    <>
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                        Loading...
                                    </>
                                ) : (
                                    "Trends"
                                )}
                            </Button>
                        </Box>

                        {/* Words by Category */}
                        {difficultyBreakdown && policy ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                <WordScoreSection
                                    level="HARD"
                                    title={`HARD Words (${wordCategories.hard.length}) - Score: ${policy.puzzleConfigure.hardScoreMin} - ${policy.puzzleConfigure.hardScoreMax}`}
                                    wordDataList={wordCategories.hard}
                                />
                                <WordScoreSection
                                    level="MEDIUM"
                                    title={`MEDIUM Words (${wordCategories.medium.length}) - Score: ${policy.puzzleConfigure.mediumScoreMin} - ${policy.puzzleConfigure.mediumScoreMax}`}
                                    wordDataList={wordCategories.medium}
                                />
                                <WordScoreSection
                                    level="EASY"
                                    title={`EASY Words (${wordCategories.easy.length}) - Score: ${policy.puzzleConfigure.easyScoreMin} - ${policy.puzzleConfigure.easyScoreMax}`}
                                    wordDataList={wordCategories.easy}
                                />
                                <WordScoreSection
                                    level="EXCLUDED"
                                    title={`Excluded Words (${wordCategories.excluded.length})`}
                                    wordDataList={wordCategories.excluded}
                                    caption={
                                        <>
                                            Words are excluded if: Score &lt; {policy.puzzleConfigure.hardScoreMin} (too rare for MEDIUM/HARD),
                                            Score &gt; {policy.puzzleConfigure.easyScoreMax} (too common/rejected),
                                            or Score between {policy.puzzleConfigure.mediumScoreMax + 1} - {policy.puzzleConfigure.easyScoreMin - 1} (gap between MEDIUM and EASY ranges)
                                        </>
                                    }
                                />
                                <WordScoreSection
                                    level="NO_SCORE"
                                    title={`Words without Score (${wordCategories.noScore.length})`}
                                    wordDataList={wordCategories.noScore}
                                />
                            </Box>
                        ) : (
                            /* Show all words when policy is not loaded yet */
                            <WordScoreSection
                                level="ALL"
                                title={`All Words (${wordScores.length})`}
                                wordDataList={wordScores}
                            />
                        )}

                        {trendsError && (
                            <Typography color="error" sx={{ mt: 2, p: 2 }}>
                                {trendsError}
                            </Typography>
                        )}
                        {ngramError && (
                            <Typography color="error" sx={{ mt: 2, p: 2 }}>
                                {ngramError}
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default WordScoreDialog;
